import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { fetchDomesByHost } from "../../api/dome.api";

const HostDomes = () => {
  const location = useLocation();
  const host = location.state?.hostData;
  console.log("🚀 ~ HostDomes ~ host:", host)

  const { background } = useContext(ThemeContext);
  const isDark = background === "dark";

  const { id: hostId } = useParams();
  const navigate = useNavigate();

  const [domes, setDomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(9);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /* ----------------------------- Fetch Domes ----------------------------- */
  const loadDomes = async (pageNumber = 1, query = "") => {
    try {
      if (pageNumber === 1) setLoading(true);
      else setIsLoadingMore(true);

      const data = await fetchDomesByHost(hostId, query, pageNumber, limit);
      if (pageNumber === 1) setDomes(data.domes || data);
      else setDomes((prev) => [...prev, ...(data.domes || data)]);

      setHasMore(data.hasNextPage || (data.domes?.length || 0) === limit);
    } catch (error) {
      console.error("Error fetching domes:", error);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    loadDomes(1, search);
  }, [search, hostId]);

  /* ----------------------------- Handlers ----------------------------- */
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDomeClick = (domeId) => {
    navigate(`${domeId}`);
  };

  const navigateToCreatePage = () => {
    if (!host) return;
    navigate("create", {
      state: {
        hostData: host,
      },
    });
  };

  const loadNextPage = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadDomes(nextPage, search);
  };

  /* ----------------------------- Loading State ----------------------------- */
  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  /* ----------------------------- Render ----------------------------- */
  return (
    <div className="container-fluid">
      {/* Header + Search */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 mb-3">Domes for Host: {host.companyName}</h4>
        <div className="d-flex gap-2 mb-3">
          <input
            type="text"
            placeholder="Search domes..."
            value={search}
            onChange={handleSearchChange}
            className="form-control"
            style={{ width: "250px", borderRadius: "5px", fontSize: "14px" }}
          />
          <button
            className="btn btn-primary"
            onClick={navigateToCreatePage}
            style={{ fontSize: "14px", borderRadius: "5px" }}
          >
            Create Dome
          </button>
        </div>
      </div>

      {/* Domes Grid */}
      <div className="row">
        {domes?.length === 0 ? (
          <div className="text-center text-gray-500 fs-14">No domes found</div>
        ) : (
          domes?.map((dome) => (
            <div key={dome._id} className="col-xl-4 col-lg-6 col-md-6 mb-3">
              <div
                className={`card mb-3 shadow-sm hover-shadow ${
                  isDark ? "bg-dark text-white" : ""
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => handleDomeClick(dome._id)}
              >
                {/* Dome Image */}
                {dome.coverImage ? (
                  <img
                    src={dome.coverImage}
                    alt={dome.domeName}
                    className="card-img-top"
                    style={{ height: "180px", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="bg-secondary d-flex align-items-center justify-content-center"
                    style={{ height: "180px" }}
                  >
                    <i className="fas fa-cube text-white fs-36"></i>
                  </div>
                )}

                {/* Dome Details */}
                <div className="card-body">
                  <h5 className="card-title text-primary mb-2">
                    {dome.domeName}
                  </h5>
                  <p className="text-secondary mb-1">{dome.domeDescription}</p>
                  <p className="mb-1">
                    <strong>Location:</strong> {dome.location || "N/A"}
                  </p>
                  <p className="mb-1">
                    <strong>Price:</strong> ${dome.price} |{" "}
                    <strong>Offer:</strong> {dome.offerPercent}%
                  </p>
                  <p className="mb-1">
                    <strong>Created:</strong>{" "}
                    {dome.createdAt
                      ? new Date(dome.createdAt).toLocaleDateString()
                      : "N/A"}
                  </p>
                  <p
                    className={`mb-0 ${
                      dome.archived ? "text-danger" : "text-success"
                    }`}
                  >
                    <strong>Status:</strong>{" "}
                    {dome.archived ? "Archived" : "Active"}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-center gap-2 my-3">
        <button
          onClick={loadNextPage}
          disabled={!hasMore || isLoadingMore}
          className="btn btn-outline-primary"
        >
          {isLoadingMore ? "Loading..." : "Load More"}
        </button>
      </div>
    </div>
  );
};

export default HostDomes;
